import {Auth} from '@domain/model/Auth/Auth';
import {AuthLoginGoogle} from '@domain/model/Auth/AuthLoginGoogle';
import {RegisterUserCommand} from '@domain/model/Auth/RegisterUserCommand';
import {ExternalLoginCommand} from '@domain/model/Auth/ExternalLoginCommand';
import {RecoverPasswordCommand} from '@domain/model/Auth/RecoverPasswordCommand';
import {fetch} from '@infra/api/fetch';
import {AuthLoginFaceBook, FacebookToken} from '@domain/model/Auth/AuthLoginFaceBook';
import {AuthError} from '@domain/model/Auth/AuthError';
import {ConfigurationApplication} from '@application/Config';
import {AppleToken} from '@domain/model/Auth/AppleToken';

const headers = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

const recoverPassword = async ({email}: RecoverPasswordCommand): Promise<Record<string, string>> => {
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify({email}),
    headers,
  };

  const response = await fetch(`/api/auth/forgotpwd/`, requestOptions);
  if (response.status === 404) {
    return Promise.reject({errorKey: response.status});
  }

  return response.json();
};

const registerUser = async (registerUserCommand: RegisterUserCommand): Promise<Auth> => {
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify({
      ...registerUserCommand,
      tradename: ConfigurationApplication.retrieve().tradename,
    }),
    headers,
  };
  const response = await fetch(`/api/register/`, requestOptions);
  if (!response.ok) {
    let code, message;
    try {
      ({code, message} = await response.json());
    } catch (e) {
      code = '';
      message = '';
    }
    return Promise.reject({errorKey: response.status, code, message});
  }
  return response.json();
};

const loginWithGoogle = async (params: AuthLoginGoogle): Promise<string> =>
  await externalLoginAuth({type: 'login', provider: 'google', body: params});

const registerWithFaceBook = async (params: AuthLoginFaceBook): Promise<string> =>
  await externalLoginAuth({type: 'register', provider: 'facebook', body: params});

const loginWithFaceBook = async (params: FacebookToken): Promise<string> =>
  await externalLoginAuth({type: 'login', provider: 'facebook', body: params});

const loginWithApple = async (params: AppleToken): Promise<string> =>
  await externalLoginAuth({type: 'login', provider: 'apple', body: params});

const externalLoginAuth = async ({type, provider, body}: ExternalLoginCommand): Promise<string> => {
  try {
    const response = await fetch(`/api/auth/${type}/${provider}`, {
      method: 'POST',
      body: JSON.stringify({...body, tradename: ConfigurationApplication.retrieve().tradename}),
      headers,
    });
    if (response.ok) {
      return (await response.json()).token as string;
    } else {
      return Promise.reject(response.status === 403 ? AuthError.USER_EXISTS : AuthError.UNKNOWN);
    }
  } catch {
    return Promise.reject(AuthError.UNKNOWN);
  }
};

export const apiAuth = {
  recoverPassword,
  loginWithGoogle,
  registerUser,
  registerWithFaceBook,
  loginWithFaceBook,
  loginWithApple,
};
